import { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import DefaultCard from '../../../components/default/DefaultCard';
import ButtonCreate from '../../../components/buttons/ButtonCreate';
import LazyLoading from '../../../components/LazyLoading';
import DefaultModal from '../../../components/default/DefaultModal';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import { DeductionRateDataForm, DeductionRateFilters } from '../../../app/models/DeductionRate';
import BonusRateDataTable from './tables/BonusRateDataTable';
import BonusRateEdit from '../subpages/BonusRateEdit';
import useBonusRateService from '../../../app/services/hooks/useBonusRateService';
import BonusRateCreate from '../subpages/BonusRateCreate';


interface Props {
    bonusId: number;
}

const BonusRateContainer = ({ bonusId }: Props) => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { getAllBonusRatesByBonus, fetchingGetBonusRatesByBonus, deleteBonusRate } = useBonusRateService();

    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [bonusRateId, setBonusRateId] = useState<number>(-1);
    const [bonusRates, setBonusRates] = useState<DeductionRateDataForm[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [filter, setFilter] = useState<DeductionRateFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (id: number) => {
        setShowingEdit(true);
        setBonusRateId(id);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setBonusRateId(-1);
    };

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const destroy = (bonusRateId: number) => {
        const _text = 'Está a punto de eliminar la tarifa de Bonificación #' + bonusRateId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando tarifa de Bonificación...');
                        deleteBonusRate(bonusRateId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La tarifa de Bonificación no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const reloadTable = () => {
        getAllBonusRatesByBonus(bonusId, {
            onSuccess: (response: ServiceResponse) => {
                setBonusRates(response.data.bonus_rates)
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
            }
        });
    };

    return (
        <>
            <DefaultCard>
                {!fetchingGetBonusRatesByBonus ? (
                    <div className="row">
                        <div className="col-12 mb-2">
                            <ButtonCreate
                                title="Nueva tarifa de Bonificación"
                                callbackCreate={showCreate}
                            />
                        </div>
                        <div className="col-12">
                            {bonusRates ? (
                                <BonusRateDataTable
                                    bonusRates={bonusRates}
                                    edit={showEdit}
                                    destroy={destroy}
                                    loading={fetchingGetBonusRatesByBonus}
                                    totalRows={totalRows}
                                    filter={filter}
                                    setFilter={setFilter}
                                />
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <LazyLoading height="300" />
                )}
            </DefaultCard>
            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Tarifa de bonificación"
                    backdrop={true}
                    showFooter={false}>
                    <BonusRateCreate
                        bonusId={bonusId}
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null} 

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Tarifa de bonificación"
                    backdrop={true}
                    showFooter={false}>
                    <BonusRateEdit
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                        bonusRateId={bonusRateId}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default BonusRateContainer;
